import { Component, OnInit, ViewChild, ElementRef, ViewEncapsulation } from '@angular/core';
import { animate, state, style, transition, trigger } from '@angular/animations';

import { DynamicDialogRef, DialogService, DynamicDialogConfig } from 'primeng/dynamicdialog';

import { HTTPService, HelperService, DataService, FormatService, ValidationService, DateService } from 'app/services';

import { mileageUnits } from 'app/constants';

import { ConnectToMotorDialogComponent } from '../connect-motor-dialog/connect-motor-dialog.component';
import { ConfirmDismissDialogComponent } from 'app/modules/shared/components';
import { TransferVehicleDialogComponent } from '../transfer-vehicle-dialog/transfer-vehicle-dialog.component';

import { debounce, find } from 'lodash';

// Interfaces for data models
interface ContactMethods {
  phone: string;
  email: string;
}
interface Customer {
  addLine1: string;
  addLine2?: string; // Optional since address2 might be empty
  city: string;
  contactMethods: ContactMethods;
  country: string;
  state: string;
  zip: string;
}
interface Vehicle {
  engine: string;
  vin: string;
  make: string;
  year: number;
  model: string;
  plateNumber: string;
  mileage: number;
  state: string;
  fleetCode: string;
}

interface Fleet {
  fleetName: string;
  fleetCode: string;
  accountType: string;
  company: string;
  phone: string;
  email: string;
}

@Component({
  selector: 'app-create-myfleet-customer-dialog',
  templateUrl: './create-myfleet-customer-dialog.component.html',
  styleUrls: ['./create-myfleet-customer-dialog.component.scss'],
  providers: [DialogService],
  encapsulation: ViewEncapsulation.None,
  animations: [
    trigger('toggleBox', [
      state(
        'open',
        style({
          height: '480px',
        }),
      ),
      state(
        'closed',
        style({
          height: '0px',
        }),
      ),
      transition('open => closed', [animate('.3s')]),
      transition('closed => open', [animate('0.3s')]),
    ]),
  ],
})
export class CreateMyFleetCustomerDialogComponent implements OnInit {
  @ViewChild('contentContainer') contentContainer!: ElementRef;
  activeIndex: number = 0;
  data: any;
  isProcessing = false;
  isOpen = false;
  customers: Fleet[] = [];
  searchQuery: string = '';
  searchVehicleQuery: string = '';
  isSearch: boolean = false;
  totalRecords: number = 0;
  loading: boolean = true;
  removeListTab: boolean = false;
  searchResults: Fleet[] = [];
  filteredListTab: boolean = true;
  fleetSuggestion: Fleet[] = [];
  vehicleSuggestion: Vehicle[] = [];
  defaultVehicleSuggestion: Vehicle[] = [];
  isLoadingFleet = false;
  contactDetails: Customer = {
    addLine1: '',
    city: '',
    state: '',
    country: '',
    zip: '',
    contactMethods: {
      phone: '',
      email: '',
    },
  };
  authorizerDetails: Customer = {
    addLine1: '',
    city: '',
    state: '',
    country: '',
    zip: '',
    contactMethods: {
      phone: '',
      email: '',
    },
  };
  shippingDetails: Customer = {
    addLine1: '',
    city: '',
    state: '',
    country: '',
    zip: '',
    contactMethods: {
      phone: '',
      email: '',
    },
  };
  basicInfo: any = {};
  fleetSettings: any = {};
  sections = [{ title: 'Basic Information' }, { title: 'Authorizer details' }, { title: 'Contact details' }, { title: 'Shipping details' }, { title: 'Fleet overrides' }, { title: 'New vehicle' }];
  isSearchingVehicleRecords = false;
  selectedVehicle: Vehicle = {} as Vehicle;
  errors: any = {};
  focus: any = {};
  openFrom = '';
  isHideVehicle = false;
  isQuickTireLookup = false;
  customer: any = {};
  vehicle: any = {};
  isCustomerSelected = false;
  customerTitle = 'New Customer';
  isCustomerDisabled = false;
  customerType = { label: 'Fleet', name: 'Fleet', value: 'fleet', icon: 'far fa-cars', placeholder: 'Fleet' };
  isTransferVehicleEnabled = this.dataService.isTransferVehicleEnabled;
  isDuplicateVehicle: boolean = false;
  vehicleAlreadyAdded = false;
  currentCustomer = {};
  customerList = [];
  showSuggestionForm = true;
  fleetName = '';
  fleetNumber = '';
  constructor(
    private dConfig: DynamicDialogConfig,
    private dynamicDialogRef: DynamicDialogRef,
    private validationService: ValidationService,
    private httpService: HTTPService,
    private helperService: HelperService,
    private formatService: FormatService,
    private dataService: DataService,
    private dialogService: DialogService,
    private dateService: DateService,
  ) {}

  async ngOnInit(): Promise<void> {
    const { fleetName, fleetNumber, openFrom, isHideVehicle, customer, vehicle, vinQuery, isQuickTireLookup } = this.dConfig.data;
    this.isQuickTireLookup = isQuickTireLookup;
    this.isHideVehicle = isHideVehicle;
    this.openFrom = openFrom;
    this.customer = customer;
    this.vehicle = vehicle;
    this.fleetName = fleetName;
    this.fleetNumber = fleetNumber;

    // Set search queries
    this.searchQuery = fleetNumber || fleetName || '';
    this.searchVehicleQuery = vehicle?.VIN || vehicle?.licensePlateNo || vinQuery || '';

    // Update sections if vehicle should be hidden
    if (isHideVehicle) this.sections.pop();

    // Determine customer selection and disable status
    this.isCustomerSelected = openFrom === 'vehicle' || (customer?.isMFCustomer && openFrom === 'finalize');
    // this.isCustomerDisabled = openFrom === 'vehicle' || (customer?.isMFCustomer && openFrom === 'finalize');
    this.isCustomerDisabled = this.isCustomerSelected;

    // Handle fleet suggestions if search query exists
    if (this.searchQuery.trim()) {
      this.focus.fleet = true;
      await this.searchFleetSuggestion(this.searchQuery);
    }

    // Focus on vehicle if opened from 'vehicle'
    if (openFrom === 'vehicle') {
      this.focus.vehicle = true;
    }
  }

  private initializeCustomer(): Customer {
    return {
      addLine1: '',
      city: '',
      state: '',
      country: '',
      zip: '',
      contactMethods: { phone: '', email: '' },
    };
  }

  resetFields(): void {
    this.isSearchingVehicleRecords = false;
    this.fleetSuggestion = [];
    this.basicInfo = {};
    this.contactDetails = this.initializeCustomer();
    this.shippingDetails = this.initializeCustomer();
    this.authorizerDetails = this.initializeCustomer();
    this.searchQuery = '';
    this.selectedVehicle = {} as Vehicle;
  }

  mapApiResponseToContact(response: any): Customer {
    return {
      addLine1: response.address1 || '',
      addLine2: response.address2 || '',
      city: response.city || '',
      contactMethods: {
        phone: response.contactMethods?.phone.split(' ')[0] || '',
        email: response.contactMethods?.email || '',
      },
      country: response.country || '',
      state: response.state || '',
      zip: response.zip || '',
    };
  }

  private extractContactMethods(fleet: any, type: string): Customer {
    const contact = fleet?.addresses.find((element: any) => element.type === type) || {};
    return this.mapApiResponseToContact(contact);
  }

  onSelectVehicleFleet(event: Vehicle): void {
    this.isSearchingVehicleRecords = false;
    this.selectedVehicle = event;
    this.showSuggestionForm = false;
    // this.vehicleSuggestion = [];
  }

  async onSelectFleet(event: any): Promise<void> {
    this.resetFields();
    this.basicInfo = {
      ...event,
      company: event?.fleet?.driverCompanyInfo?.company,
      driverCompanyInfo: event?.driverCompanyInfo,
    };

    this.contactDetails = this.extractContactMethods(event.fleet, 'contact');
    this.shippingDetails = this.extractContactMethods(event.fleet, 'billing');
    this.authorizerDetails = event?.fleet?.authorizers[0] || {};
    if (!this.isHideVehicle) {
      await this.searchFleetVehicle();
    }
  }

  async searchFleetVehicle() {
    this.isSearchingVehicleRecords = true;
    try {
      const response = await this.httpService.searchMyFleetCenter(this.basicInfo.fleetCode, true);
      response &&
        response.map((fleet: any) => {
          if ((fleet.fleetName === this.fleetName && this.fleetNumber === fleet.fleetCode) || !this.customer) {
            this.defaultVehicleSuggestion =
              fleet?.vehicles &&
              fleet?.vehicles.map((vehicle: any) => ({
                engine: vehicle.engine,
                vin: vehicle.vin,
                make: vehicle.make,
                year: vehicle.year,
                model: vehicle.model,
                plateNumber: vehicle.license.plateNumber,
                mileage: vehicle.mileage,
                state: vehicle.license.state,
                fleetCode: this.basicInfo.fleetCode,
              }));

            this.vehicleSuggestion = this.helperService.deepCopy(this.defaultVehicleSuggestion || []);
            if (this.searchVehicleQuery?.trim()) {
              this.focus.vehicle = true;
              this.searchFleetVehicleSuggestion(this.searchVehicleQuery);
            }
          }
        });
    } catch (error) {
      this.helperService.showToaster('error', error);
    } finally {
      this.isSearchingVehicleRecords = false;
    }
  }

  handleShowSuggestion() {
    this.showSuggestionForm = true;
  }

  async searchFleetSuggestion(event: string) {
    this.isLoadingFleet = true;
    try {
      const response = await this.httpService.searchMyFleetCenter(event, false);
      this.fleetSuggestion =
        response &&
        response.map((fleet: any) => {
          if ((fleet.fleetName === this.fleetName && this.fleetNumber === fleet.fleetCode) || !this.isCustomerDisabled) {
            const contactType: Customer = this.extractContactMethods(fleet, 'contact');
            return {
              driverCompanyInfo: fleet.driverCompanyInfo.requirements || [],
              fleetName: fleet.fleetName,
              fleetCode: fleet.fleetCode,
              accountType: fleet.accountType,
              company: fleet.driverCompanyInfo.company || 'N/A',
              phone: this.helperService.formatPhoneNumber(contactType?.contactMethods.phone) || null,
              email: contactType.contactMethods.email || null,
              phoneLabel: contactType?.contactMethods.phone.split(' ')[1],
              fleet,
              requirements: fleet.requirements || [],
            };
          }
        });
      const findCustomer = this.fleetSuggestion.find((customer) => customer?.fleetCode);
      if (this.isCustomerSelected && findCustomer) {
        this.isCustomerSelected = false;
        this.onSelectFleet(findCustomer);
      }
    } catch (error) {
      this.helperService.showToaster('error', error);
    } finally {
      this.isLoadingFleet = false;
    }
  }

  // onScroll = debounce((event: any) => {
  //   const container = this.contentContainer.nativeElement;
  //   const sections = container.querySelectorAll('.sections-wrapper');
  //   const threshold = 100;
  //   sections.forEach((section: HTMLElement, index: number) => {
  //     const rect = section.getBoundingClientRect();
  //     const containerRect = container.getBoundingClientRect();
  //     if (rect.top >= containerRect.top && rect.bottom <= containerRect.bottom) {
  //       this.activeIndex = index;
  //     } else if (rect.top >= containerRect.top && rect.top <= containerRect.top + threshold) {
  //       this.activeIndex = index;
  //     }
  //   });
  // }, 50);
  onScroll = debounce((event: any) => {
    const container = this.contentContainer.nativeElement;
    const sections = container.querySelectorAll('.sections-wrapper');
    const containerRect = container.getBoundingClientRect();

    let activeSectionIndex = -1;

    sections.forEach((section: HTMLElement, index: number) => {
      const rect = section.getBoundingClientRect();

      // Checking if the section is in view and adjusting the top boundary more flexibly
      const isInView = rect.top <= containerRect.top + 150 && rect.bottom >= containerRect.top + 50;

      if (isInView) {
        activeSectionIndex = index;
      }
    });

    // If scrolled all the way to the bottom, select the last section
    const isAtBottom = container.scrollHeight - container.scrollTop < container.clientHeight + 1;

    // Ensure we update activeIndex only if there's a valid section in view
    if (isAtBottom) {
      this.activeIndex = sections.length - 1; // Select the last section
    } else if (activeSectionIndex !== -1) {
      this.activeIndex = activeSectionIndex;
    }
  }, 50);

  handleCloseModal(data = {}): void {
    this.dynamicDialogRef.close(data);
  }

  scrollToContent(index: number): void {
    setTimeout(() => {
      const element = document.getElementById(`content-${index}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
        this.updateActiveIndex(index);
      }
    }, 50);
  }

  searchFleetVehicleSuggestion(query: string): void {
    if (!query) {
      this.vehicleSuggestion = this.helperService.deepCopy(this.defaultVehicleSuggestion);
    }
    this.vehicleSuggestion = this.defaultVehicleSuggestion.filter(({ vin }) => new RegExp(query, 'i').test(vin));
  }

  updateActiveIndex(index: number): void {
    this.activeIndex = index;
  }

  handleSearchValue(query: string, isCustomer: boolean = false): void {
    if (isCustomer) {
      this.searchQuery = query.trim();
    } else {
      this.searchVehicleQuery = query.trim();
    }
  }

  handleSubmit(): void {
    if (!this.isCustomerDisabled) {
      if (!this.validation() || this.isProcessing) return;
      this.isProcessing = true;

      let fleetSettings = this.formatService.formatFleetSettings(this.fleetSettings);
      if (this.customer?._id) {
        fleetSettings = this.getFleetSettings();
      }

      const payload = {
        MFC: {
          companyName: this.basicInfo?.company || '',
          vehicles: [this.selectedVehicle],
          shippingDetails: this.shippingDetails,
          authorizerDetails: this.authorizerDetails,
          contactDetails: this.contactDetails,
          fleetSettings: this.fleetSettings,
          accountType: this.basicInfo.accountType,
          requirements: [...this.basicInfo.driverCompanyInfo, ...this.basicInfo.requirements],
        },
        address: this.getAddress(),
        businessAddress: this.getAddress(),
        fleetNumber: this.basicInfo?.fleetCode || '',
        fleetCode: this.basicInfo?.fleetCode || '',
        fullNameQBO: this.basicInfo?.fleetName || '',
        fullName: this.basicInfo?.fleetName || '',
        firstName: this.basicInfo?.fleetName || '',
        isFleetCustomer: true,
        isActive: true,
        customerType: this.customerType,
        emails: this.getContactEmailAndPhone().emails,
        phones: this.getContactEmailAndPhone().phones,
        isMFCustomer: true,
        billingType: 'my-fleet-center',
        ...fleetSettings,
        chargeAccountEnable: false,
      };

      if (this.dataService.isChargeAccountEnabled) {
        payload.chargeAccountEnable = true;
      }

      this.customer && this.customer?._id ? this.updateCustomer(payload) : this.createCustomer(payload);
    } else {
      this.isProcessing = true;
      if (this.vehicle?._id) {
        this.onUpdateVehicle();
      } else {
        this.createVehicle(this.customer?._id);
      }
    }
  }

  searchVehicle() {
    const state = this.selectedVehicle?.state || this.helperService?.getCompany()?.stateCode;
    const queries = [this.selectedVehicle?.vin, this.selectedVehicle?.plateNumber, `${this.selectedVehicle.year || ''} ${this.selectedVehicle.make || ''} ${this.selectedVehicle.model || ''}`];

    // Filter non-empty queries and map to API calls
    const searchRequests = queries.filter((query) => query).map((query) => () => this.httpService.searchMotorVehicle(query, state));

    // Run requests one by one until valid data is found
    this.runSequentially(searchRequests)
      .then((result) => {
        if (result) {
          this.processVehicleData(result.Vehicles);
        }
        this.isProcessing = false;
        const response = {
          isVehicleUpdated: true,
          fleetNumber: this.basicInfo.fleetCode,
          vehicle: this.vehicle,
        };
        this.handleCloseModal(response);
      })
      .catch((error) => {
        this.handleCloseModal();
        console.error('Error fetching vehicle data:', error);
      });
  }

  runSequentially(requests: (() => Promise<any>)[]): Promise<any> {
    return requests.reduce((prev, curr) => {
      return prev.then((result) => (result?.Vehicles?.length ? result : curr()));
    }, Promise.resolve(null));
  }

  processVehicleData(Vehicles) {
    if (Vehicles?.length > 0) {
      this.dialogService.open(ConnectToMotorDialogComponent, {
        styleClass: 'add-dialog connect-to-motor',
        showHeader: false,
        data: {
          customer: this.customer,
          isVehicleUpdated: true,
          vehicle: { ...Vehicles[0], _id: this.vehicle._id },
        },
      });
    }
    const payload = {
      fleetNumber: this.basicInfo.fleetCode,
      customer: this.customer,
      isVehicleUpdated: true,
      vehicle: { ...this.vehicle, _id: this.vehicle._id },
    };
    this.handleCloseModal(payload);
  }

  setContactDetail(type: string, address: any): any {
    return {
      contactType: type === 'phone' ? 'mobile' : 'email',
      contactSource: 'MFC',
      isActive: true,
      isMarketingNotifEnabled: true,
      isOperationalNotifEnabled: true,
      isPrimary: false,
      label: '',
      type: address.type,
      value: type === 'phone' ? this.helperService.removePhoneFormatting(address?.contactMethods?.[type]) : address?.contactMethods?.[type],
    };
  }

  createVehicle(customerId) {
    const vehicleData = {
      name: `${this.selectedVehicle.year || ''} ${this.selectedVehicle.make || ''} ${this.selectedVehicle.model || ''}`,
      vehicleName: `${this.selectedVehicle.year || ''} ${this.selectedVehicle.make || ''} ${this.selectedVehicle.model || ''}`,
      VIN: this.selectedVehicle.vin,
      plate: '',
      year: this.selectedVehicle.year || '',
      make: this.selectedVehicle.make || '',
      ODM: (this.selectedVehicle.mileage && [{ reading: this.selectedVehicle.mileage, readingDate: this.dateService.currentDate(), mileageMeasuringUnit: 'km' }]) || [],
      trim: '',
      model: this.selectedVehicle.model || '',
      bodyStyle: '',
      licensePlateNo: this.selectedVehicle.plateNumber,
      isMotorConnected: false,
      isCarfaxConnected: true,
      apiResponse: null,
      customerId: customerId,
      customerName: this.basicInfo?.fleetName,
      registrationState: this.selectedVehicle.state,
      registrationStateCode: this.selectedVehicle.state,
      vehicleCheck: true,
      isMFConnected: true,
      fleetCode: this.basicInfo.fleetCode,
      connectedVia: 'vin',
    };
    this.httpService.createVehicles(vehicleData).then(
      (vehicle) => {
        this.vehicle = vehicle;
        if (vehicle.apiResponse != null && !vehicle?.apiResponse?.BaseVehicleID) {
          this.helperService.motorEventGenerator(vehicle, 'notFound', 'Motor: Vehicle Not Found');
        } else {
          if (vehicle?.apiResponse?.BaseVehicleID) {
            this.helperService.motorEventGenerator(vehicle, 'found', 'Vehicle Found - Motor');
            if (vehicle._id) {
              this.httpService.getVehicleFluidSpecifications(vehicle._id).then((data: any) => {
                if (!data.fluidSpecs || !data.fluidSpecs.length) {
                  this.helperService.motorEventGenerator(vehicle, 'notFound', 'Motor: Engine Specifications not Found');
                }
              });
            }
          }
        }
        // if (!this.vehicle.isMotorConnected) {
        this.searchVehicle();
        // }

        if (!vehicle.isMotorAndCarfaxSynced) {
        } else {
          this.dataService.sendData(this.dataService.typeUpdate('vehile-side-list', 'vehicle', vehicle));
        }
        // this.dataService.sendData(this.dataService.typeUpdate('customer-list', 'customer', {}));
        this.dataService.sendData(
          this.dataService.typePassData('vehicle-for-estimate', {
            vehicle: {
              vehicleId: this.vehicle._id,
              name: this.vehicle.name,
              vehicle: this.vehicle || {},
            },
          }),
        );
        // this is to add new added vehicle in the list for customer center
        this.dataService.sendData(
          this.dataService.typePassData('vehicle-list-customer-center', {
            vehicle: {
              vehicleId: this.vehicle._id,
              name: this.vehicle.name,
              vehicle: this.vehicle || {},
            },
          }),
        );

        if (this.openFrom === 'vehicle-side-list') {
          this.dataService.sendData(
            this.dataService.typeSidebar('vehicle', this.customer._id, this.customer.fullName, {
              _id: this.customer._id,
              vehicle,
            }),
          );
        }

        if (this.openFrom === 'add-vehicle') {
        }

        this.helperService.showToaster('success', 'Vehicle created');
        if (this.openFrom === 'add-customer') {
          this.dataService.sendData(
            this.dataService.typeAdd('vehicle-added', {
              vehicle,
              event: 'vehicle_created',
              followUps: this.vehicle.followUps,
              customerId: this.vehicle.customerId,
            }),
          );
        }

        if (this.vehicle.followUps && this.vehicle.followUps.length) {
          if (this.openFrom !== 'add-customer') {
            this.dataService.sendData(
              this.dataService.typeFollowUp('RO', {
                vehicle,
                event: 'vehicle_created',
                followUps: this.vehicle.followUps,
                customerId: this.vehicle.customerId,
              }),
            );
          }
        }
      },
      (err) => {
        console.log(err);
        this.isProcessing = false;

        if (this.isTransferVehicleEnabled) {
          this.errors.transferVehicleError = `Vehicle already registered against customer`;
        }
        this.vehicleAlreadyAdded = true;
        this.currentCustomer = err.data.currentCustomer;
        if (err.data.customers) {
          err.data.customers.forEach((c) => {
            if (c.vehicles) {
              c.vehicles.map((v) => {
                return (v.isChecked = false);
              });
            }
            this.customerList.push(c);
          });
          if (this.isTransferVehicleEnabled) {
            if (err.data.differentUser === false) {
              this.handleVehicleDuplication(err.data.customers[err.data.customers.length - 1].vehicles);
            } else if (err.data.differentUser || !err.data.differentUser) {
              this.handleOpenTransferVehicleDialog();
            }
          } else {
            // this.openDuplicateVehicleModal();
          }
        }
      },
    );
  }

  handleOpenTransferVehicleDialog() {
    const ref = this.dialogService.open(TransferVehicleDialogComponent, {
      width: '740px',
      styleClass: 'add-dialog transfer-vehicle-dialog',
      showHeader: false,
      data: {
        customerList: this.customerList,
        customerId: this.customer._id,
        vehicleName: `${this.selectedVehicle.year || ''} ${this.selectedVehicle.make || ''} ${this.selectedVehicle.model || ''}`,
        currentCustomer: this.currentCustomer,
        isShow: false,
        isMFConnected: true,
      },
    });

    const onClose = ref.onClose.subscribe((data) => {
      if (data.type === 'updateVehicle') {
        this.updateVehicleSidebar(data);
        const response = {
          isVehicleUpdated: true,
          fleetNumber: this.basicInfo.fleetCode,
          vehicle: data.selectedVehicle,
        };
        this.handleCloseModal(response);
      }
      onClose.unsubscribe();
    });
  }

  handleVehicleDuplication(vehicleData) {
    const ref = this.dialogService.open(ConfirmDismissDialogComponent, {
      width: '30%',
      styleClass: 'small-heading-dialog',
      showHeader: false,
      data: {
        styleClassHeading: 'font-14',
        title: `Vehicle duplication`,
        message: `This vehicle cannot be created as a new vehicle because it already exists under this customer's name. However, you can proceed by updating the existing information for this vehicle`,
        button: {
          success: { title: 'Change & update', styleClass: 'btn-danger' },
          cancel: { title: 'Go back' },
        },
        headerIcon: 'fas fa-exclamation font-icon-red font-white',
      },
    });

    const onClose = ref.onClose.subscribe((close: any) => {
      if (close.isSuccess) {
        const data = { selectedVehicle: vehicleData[0] };
        this.updateVehicleSidebar(data, true);
        const response = {
          isVehicleUpdated: true,
          fleetNumber: this.basicInfo.fleetCode,
          vehicle: vehicleData[0],
        };
        this.handleCloseModal(response);
      }
      onClose.unsubscribe();
    });
  }

  updateVehicleSidebar(data, isVehicleDuplicate = false) {
    if (data.selectedVehicle) {
      if (this.isQuickTireLookup) {
        this.customer.fullName = data.selectedVehicle.customerName || data.selectedVehicle.currentCustomerName || this.customer.fullName;
        this.vehicle = {
          ODM: data.selectedVehicle.ODM,
          VIN: data.selectedVehicle.VIN,
          apiResponse: data.selectedVehicle.apiResponse,
          color: { name: data.selectedVehicle.color || '' },
          connectedVia: data.selectedVehicle.connectedVia,
          customerId: data.selectedVehicle.customerId,
          _id: data.selectedVehicle._id,
          driveType: { name: data.selectedVehicle.driveType || '' },
          engine: data.selectedVehicle.engine,
          isActive: true,
          isCarfaxConnected: data.selectedVehicle.isCarfaxConnected,
          isMotorAndCarfaxSynced: data.selectedVehicle.isMotorAndCarfaxSynced,
          isMotorConnected: data.selectedVehicle.isMotorConnected,
          licensePlateNo: data.selectedVehicle.licensePlateNo || '',
          make: data.selectedVehicle.make,
          model: data.selectedVehicle.model,
          name: data.selectedVehicle.name,
          notes: data.selectedVehicle.notes || '',
          odmHistory: data.selectedVehicle.odmHistory || [],
          registrationState: data.selectedVehicle.registrationState || '',
          registrationStateCode: data.selectedVehicle.registrationStateCode || '',
          tags: data.selectedVehicle.tags || [],
          vehicleCheck: data.selectedVehicle.vehicleCheck,
          vehicleName: data.selectedVehicle.vehicleName,
          vin: data.selectedVehicle.VIN,
          year: data.selectedVehicle.year,
          vehicleId: data.selectedVehicle._id,
          title: data.selectedVehicle.vehicleName,
          vehicle: data.selectedVehicle.vehicleName,
          fullName: data.selectedVehicle.customerName,
          type: 'vehicle-add',
        };
      } else {
        this.customer.fullName = data.selectedVehicle.customerName;
        this.vehicle = {
          ODM: data.selectedVehicle.ODM,
          VIN: data.selectedVehicle.VIN,
          apiResponse: data.selectedVehicle.apiResponse,
          color: { name: data.selectedVehicle.color || '' },
          connectedVia: data.selectedVehicle.connectedVia,
          _id: data.selectedVehicle.customerId,
          driveType: { name: data.selectedVehicle.driveType || '' },
          engine: data.selectedVehicle.engine,
          isActive: true,
          isCarfaxConnected: data.selectedVehicle.isCarfaxConnected,
          isMotorAndCarfaxSynced: data.selectedVehicle.isMotorAndCarfaxSynced,
          isMotorConnected: data.selectedVehicle.isMotorConnected,
          licensePlateNo: data.selectedVehicle.licensePlateNo || '',
          make: data.selectedVehicle.make,
          model: data.selectedVehicle.model,
          name: data.selectedVehicle.name,
          notes: data.selectedVehicle.notes || '',
          odmHistory: data.selectedVehicle.odmHistory || [],
          registrationState: data.selectedVehicle.registrationState || '',
          registrationStateCode: data.selectedVehicle.registrationStateCode || '',
          tags: data.selectedVehicle.tags || [],
          vehicleCheck: data.selectedVehicle.vehicleCheck,
          vehicleName: data.selectedVehicle.vehicleName,
          vin: data.selectedVehicle.VIN,
          year: data.selectedVehicle.year,
          vehicleId: data.selectedVehicle._id,
          title: data.selectedVehicle.vehicleName,
          vehicle: data.selectedVehicle.vehicleName,
          fullName: data.selectedVehicle.customerName,
          type: 'vehicle-add',
        };
      }

      if (data.selectedVehicle.transmission) {
        this.vehicle.transmission = { label: data.selectedVehicle.transmission === 'automatic' ? 'Automatic' : 'Manual', value: data.selectedVehicle.transmission || '' };
      }

      if (data.selectedVehicle.ODM && data.selectedVehicle.ODM.length) {
        let { reading, mileageMeasuringUnit } = data.selectedVehicle.ODM.pop();

        this.vehicle.mileage = `${reading || ''}`;

        if (reading) {
          // if (!mileageMeasuringUnit) {
          //   mileageMeasuringUnit = this.defaultMileageUnit;
          // }

          this.vehicle.mileageMeasuringUnit = find(mileageUnits, {
            value: mileageMeasuringUnit,
          });
        }
      }

      this.errors.vin = '';
      this.errors.licensePlateNo = '';
    }
    this.isDuplicateVehicle = true;
    this.errors.vin = '';
    this.errors.licensePlateNo = '';

    // if (this.openFrom === 'quick-tire-lookup') {
    // this.onCustomerVehicleSelect(this.vehicle);
    //   this.isCreateVehicleMode = false;
    //   this.vehicleProcessingMode.emit(this.isCreateVehicleMode);
    // }
    // else {
    // if (isVehicleDuplicate) {
    this.dataService.sendData(this.dataService.typePassData('vehicle-transfer', {}));
    // } else {
    // if (this.openFrom !== 'add-appointment') {
    // this.vehicleCreated.emit({
    //   customer: {
    //     _id: this.customer._id,
    //     fullName: this.customer.fullName,
    //   },
    //   vehicle: {
    //     _id: this.vehicle.vehicleId,
    //     name: this.vehicle.name,
    //     vehicle: {
    //       ...this.vehicle,
    //       _id: this.vehicle.vehicleId,
    //     },
    //   },
    //   openFrom: this.openFrom,
    // });
    // }
    // this.dataService.sendData(this.dataService.typePassData('vehicle-transfer', {}));
    // }

    this.dataService.sendData(
      this.dataService.typePassData('vehicle-list-customer-center', {
        vehicle: {
          vehicleId: this.vehicle._id,
          name: this.vehicle.name,
          vehicle: this.vehicle || {},
        },
      }),
    );

    if (this.openFrom !== 'add-appointment' && !this.isQuickTireLookup) {
      this.dataService.sendData(this.dataService.typeSidebar('vehicle', this.vehicle._id, this.vehicle.fullName, this.vehicle));
    }
  }

  updateCustomerContact(customerId): void {
    const contactsDetails = {
      contacts: [],
    };
    this.basicInfo?.fleet?.addresses?.forEach((address: any) => {
      if (address.contactMethods) {
        const { email, phone } = address.contactMethods;
        if (email) contactsDetails.contacts.push(this.setContactDetail('email', address));
        if (phone) contactsDetails.contacts.push(this.setContactDetail('phone', address));
      }
    });
    if (this.authorizerDetails?.contactMethods) {
      const { email, phone } = this.authorizerDetails.contactMethods;
      if (email) contactsDetails.contacts.push(...this.getContactEmailAndPhone().emails);
      if (phone) contactsDetails.contacts.push(...this.getContactEmailAndPhone().phones);
    }
    this.httpService
      .updateContacts(customerId, { contacts: contactsDetails.contacts })
      .then()
      .catch((err) => {
        this.helperService.showToaster('error', err);
      });
  }

  onClearSuggestion(isCustomer: boolean = false): void {
    if (isCustomer) {
      this.searchQuery = '';
      this.fleetSuggestion = [];
    } else {
      this.searchVehicleQuery = '';
      this.vehicleSuggestion = this.helperService.deepCopy(this.defaultVehicleSuggestion);
    }
  }

  updateFleetSettings({ fleetSettings }: { fleetSettings: any }): void {
    this.fleetSettings = fleetSettings;
  }

  disconnectVehicles(customerId) {
    const payload = {
      _id: customerId,
    };
    this.httpService
      .disconnectMFCVehicles(payload)
      .then((response) => {
        this.dataService.sendData(this.dataService.typeUpdate('vehile-side-list', 'vehicle', customerId));
      })
      .catch((err) => {
        this.helperService.showToaster('error', err);
      });
  }

  private async updateCustomer(payload: any): Promise<void> {
    payload._id = this.customer._id;
    try {
      const response = await this.httpService.updateCustomer(payload);
      this.helperService.showToaster('success', 'Successfully updated');
      this.updateCustomerContact(response.customer._id);
      if (!this.isHideVehicle && this.selectedVehicle.vin) {
        if (this.vehicle?._id) {
          this.onUpdateVehicle();
        } else {
          this.createVehicle(response.customer._id);
        }
      } else {
        if (this.isHideVehicle) {
          this.disconnectVehicles(response.customer._id);
        }
      }
      let callBackResponse = {
        ...response.customer,
        email: this.authorizerDetails?.contactMethods?.email,
        phone: this.authorizerDetails?.contactMethods?.phone,
      };
      if (this.openFrom === 'finalize') {
        callBackResponse = {
          customer: {
            ...response.customer,
            email: this.authorizerDetails?.contactMethods?.email,
            phone: this.authorizerDetails?.contactMethods?.phone,
          },
          vehicle: this.vehicle,
        };
      }
      this.isProcessing = false;
      this.handleCloseModal(callBackResponse);
    } catch (error) {
      this.helperService.showToaster('error', error);
      this.isProcessing = false;
    }
  }

  private async createCustomer(payload: any): Promise<void> {
    try {
      const response = await this.httpService.createCustomer(payload);
      this.helperService.showToaster('success', 'Successfully created');
      if (!this.isHideVehicle) {
        this.createVehicle(response.customer._id);
      }
      this.updateCustomerContact(response._id);
      let callBackResponse = {
        ...response.customer,
        email: this.authorizerDetails?.contactMethods?.email,
        phone: this.authorizerDetails?.contactMethods?.phone,
      };
      if (this.openFrom === 'finalize') {
        callBackResponse = {
          customer: {
            ...response.customer,
            email: this.authorizerDetails?.contactMethods?.email,
            phone: this.authorizerDetails?.contactMethods?.phone,
          },
          vehicle: response.vehicle,
        };
      }
      this.handleCloseModal(response);
    } catch (error) {
      this.helperService.showToaster('error', error);
      this.isProcessing = false;
    }
  }

  getContactEmailAndPhone() {
    const emails = this.authorizerDetails?.contactMethods?.email
      ? [
          {
            contactType: 'email',
            isActive: true,
            isMarketingNotifEnabled: true,
            isOperationalNotifEnabled: true,
            isPrimary: true,
            label: '',
            value: this.authorizerDetails?.contactMethods?.email || '',
          },
        ]
      : [];
    const phones = this.authorizerDetails?.contactMethods?.phone
      ? [
          {
            contactType: 'mobile',
            isActive: true,
            isMarketingNotifEnabled: true,
            isOperationalNotifEnabled: true,
            isPrimary: true,
            label: this.basicInfo?.phoneLabel || '',
            value: this.authorizerDetails?.contactMethods?.phone || '',
          },
        ]
      : [];
    return { emails, phones };
  }

  private getAddress(): any {
    return {
      stateCode: this.contactDetails.state,
      phone: this.contactDetails.contactMethods.phone,
      email: this.contactDetails.contactMethods.email,
      ...this.contactDetails,
    };
  }

  private getFleetSettings(): any {
    return !this.fleetSettings || Object.keys(this.fleetSettings).length === 0
      ? {
          customLaborRate: null,
          customPartPricingMatrix: null,
          customTaxCode: null,
          customOrderDiscount: null,
          customShopSupplies: null,
          isLaborRateOverride: false,
          isOrderDiscOverride: false,
          isPricingMatrixOverride: false,
          isShopSuppliesOverride: false,
          isTaxCodeOverride: false,
        }
      : this.formatService.formatFleetSettings(this.fleetSettings);
  }

  validation(): boolean {
    return this.validationService.validateCustomFleetSettings(this.fleetSettings, this.errors);
  }

  myFleetTitle() {
    if (this.customer?.isMFCustomer) {
      this.customerTitle = 'Update Customer';
      return 'Update customer with MyFleetCenter';
    } else if (this.customer?._id && !this.customer?.isMFCustomer) {
      this.customerTitle = 'Connect Customer';
      return 'Connect customer to MyFleetCenter';
    } else {
      this.customerTitle = 'New  Customer';
      return 'Create new fleet with MyFleetCenter';
    }
  }

  onUpdateVehicle() {
    const vehicle = {
      name: `${this.selectedVehicle.year || ''} ${this.selectedVehicle.make || ''} ${this.selectedVehicle.model || ''}`,
      vehicleName: `${this.selectedVehicle.year || ''} ${this.selectedVehicle.make || ''} ${this.selectedVehicle.model || ''}`,
      VIN: this.selectedVehicle.vin,
      plate: '',
      year: this.selectedVehicle.year || '',
      make: this.selectedVehicle.make || '',
      ODM: (this.selectedVehicle.mileage && [{ reading: this.selectedVehicle.mileage, readingDate: this.dateService.currentDate(), mileageMeasuringUnit: 'km' }]) || [],
      trim: '',
      model: this.selectedVehicle.model || '',
      bodyStyle: '',
      licensePlateNo: this.selectedVehicle.plateNumber,
      isMotorConnected: false,
      isCarfaxConnected: true,
      apiResponse: null,
      customerId: this.customer._id,
      customerName: this.basicInfo?.fleetName,
      registrationState: this.selectedVehicle.state,
      registrationStateCode: this.selectedVehicle.state,
      vehicleCheck: false,
      isMFConnected: true,
      fleetCode: this.basicInfo.fleetCode,
      isVehicleTransfer: false,
      isTransferActive: false,
      _id: this.vehicle._id,
    };

    //isTransferVehicleEnabled feature flag, and updating the vehicle
    if (this.dataService.isTransferVehicleEnabled) {
    } else {
      vehicle.isVehicleTransfer = false;
      //commented code due to testing
      if (!this.isDuplicateVehicle) {
        vehicle.vehicleCheck = true;
      } else {
        vehicle.vehicleCheck = false;
      }
    }

    this.httpService.updateVehicle(vehicle).then(
      (vehicle) => {
        this.vehicle = vehicle.vehicle;
        this.searchVehicle();
        this.helperService.showToaster('success', 'Vehicle saved');
        this.dataService.sendData(this.dataService.typePassData('vehicle', { _id: this.customer._id, vehicle: vehicle.vehicle, from: '' }));
        if (vehicle?.isTagUpdated && vehicle?.followUps?.length) {
          this.dataService.sendData(
            this.dataService.typeFollowUp('RO', {
              vehicle,
              event: 'vehicle_created',
              followUps: vehicle?.followUps,
              customerId: this.customer._id,
            }),
          );
        }
        const response = {
          isVehicleUpdated: true,
          vehicle: vehicle?.vehicle,
        };
        this.dataService.sendData(this.dataService.typePassData('vehicle-update-finalize', { vehicle: response.vehicle }));
      },
      (err) => {
        console.log(err);
        this.isProcessing = false;
        if (typeof err === 'string') {
          this.helperService.showToaster('error', err);
        }
      },
    );
  }
}
